import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageSeo from '@/components/feature/PageSeo';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PostCard from '@/components/feature/PostCard';
import { posts } from '@/mocks/posts';
import { getPostById, getRelatedPosts } from '@/lib/content';

export default function PostPage() {
  const { id = '' } = useParams();
  const post = getPostById(id);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post?.likes ?? 0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLiked(false);
    setLikeCount(post?.likes ?? 0);
    setCopied(false);
  }, [id, post]);

  const related = useMemo(
    () => (post ? getRelatedPosts(post.id, post.channel) : []),
    [post],
  );

  const hotList = useMemo(
    () => [...posts].sort((a, b) => b.heat - a.heat).slice(0, 6),
    [],
  );

  const handleLike = () => {
    setLiked((prev) => {
      setLikeCount((count) => (prev ? count - 1 : count + 1));
      return !prev;
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!post) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto flex max-w-[1400px] flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line w-8 h-8 flex items-center justify-center text-4xl" />
          </span>
          <h1 className="mt-6 font-heading text-2xl text-foreground-950">这条瓜好像被吃完了</h1>
          <p className="mt-3 text-sm text-foreground-600">内容可能已下线，或者链接不太对。</p>
          <Link
            to="/"
            className="mt-8 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            回到首页
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    dateModified: post.date,
    inLanguage: 'zh-CN',
    articleSection: post.channelName,
    keywords: post.tags.join(','),
    author: { '@type': 'Person', name: post.author },
    publisher: { '@type': 'Organization', name: '糖心吃瓜网' },
    mainEntityOfPage: `https://renrenmingxiao.cn/post/${post.id}`,
  };

  return (
    <>
      <PageSeo
        title={`${post.title} - 糖心吃瓜网`}
        description={post.excerpt}
        keywords={post.tags.join(',')}
        canonical={`https://renrenmingxiao.cn/post/${post.id}`}
        jsonLd={articleJsonLd}
      />
      <ScrollToTop />
      <SiteHeader />
      <Breadcrumb
        items={[
          { label: post.channelName, to: `/channel/${post.channel}` },
          { label: post.title },
        ]}
      />

      <main className="mx-auto max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid gap-10 lg:grid-cols-12">
          <article className="lg:col-span-8">
            <header>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/channel/${post.channel}`}
                  className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800 transition-colors hover:bg-accent-200"
                >
                  {post.channelName}
                </Link>
                {post.tags.map((tag) => (
                  <span key={tag} className="text-xs text-foreground-500">
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="mt-4 font-heading text-2xl leading-snug text-foreground-950 md:text-4xl">
                {post.title}
              </h1>

              <div className="mt-5 flex flex-wrap items-center gap-4 border-y border-background-200/70 py-4 text-sm text-foreground-500">
                <span className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 font-heading text-primary-700">
                    {post.author.slice(0, 1)}
                  </span>
                  {post.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" />
                  {post.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center" />
                  {post.readMinutes} 分钟读完
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center" />
                  热度 {post.heat}
                </span>
              </div>
            </header>

            <figure className="mt-7 overflow-hidden rounded-2xl bg-background-200">
              <img
                src={post.cover}
                alt={`${post.title} - 糖心吃瓜网内容配图`}
                title={`${post.title} 糖心吃瓜网`}
                className="h-auto w-full object-top object-cover"
              />
            </figure>

            <div className="mt-8 space-y-5">
              {post.paragraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-[15px] leading-8 text-foreground-800 md:text-base"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {post.highlight && (
              <blockquote className="mt-8 rounded-2xl border-l-4 border-primary-400 bg-primary-50/70 px-6 py-5">
                <i className="ri-double-quotes-l w-6 h-6 flex items-center justify-center text-2xl text-primary-400" />
                <p className="mt-2 font-heading text-lg leading-relaxed text-foreground-900">
                  {post.highlight}
                </p>
              </blockquote>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-background-200/70 pt-6">
              <button
                type="button"
                onClick={handleLike}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm transition-colors ${
                  liked
                    ? 'bg-accent-500 font-medium text-background-50'
                    : 'border border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-700'
                }`}
              >
                <i
                  className={`${
                    liked ? 'ri-heart-fill' : 'ri-heart-line'
                  } w-4 h-4 flex items-center justify-center text-base`}
                />
                {liked ? '已笑到' : '笑到了'}
                <span>{likeCount}</span>
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                <i className="ri-link w-4 h-4 flex items-center justify-center text-base" />
                {copied ? '链接已复制' : '复制链接'}
              </button>
              <span className="ml-auto text-xs text-foreground-500">
                内容仅供娱乐，请理性吃瓜
              </span>
            </div>

            <section className="mt-12">
              <h2 className="font-heading text-xl text-foreground-950">相关推荐</h2>
              <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <PostCard key={item.id} post={item} />
                ))}
              </div>
            </section>
          </article>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
                  <i className="ri-fire-line w-4 h-4 flex items-center justify-center text-lg text-accent-500" />
                  热瓜榜
                </h2>
                <ol className="mt-4 space-y-3">
                  {hotList.map((item, index) => (
                    <li key={item.id}>
                      <Link
                        to={`/post/${item.id}`}
                        className="group flex items-start gap-3"
                      >
                        <span
                          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                            index < 3
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-background-100 text-foreground-500'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="line-clamp-2 text-sm leading-snug text-foreground-700 group-hover:text-primary-700">
                          {item.title}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-2xl bg-foreground-950 p-5">
                <h2 className="font-heading text-base text-background-50">摸鱼提醒</h2>
                <p className="mt-2 text-sm leading-relaxed text-background-300">
                  看完这条就回去工作吧，或者……再看一条也行，反正老板也在看。
                </p>
                <Link
                  to="/channel/moyu"
                  className="mt-4 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  去摸鱼段子
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </>
  );
}